import { useMemo, useEffect, useState, useRef } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import { refreshFinkedaByUrn } from "../api";

const SUCCESS_RE = /success|paid|completed|captured|approved/i;
const FAILED_RE = /fail|declin|cancel|error|reject/i;

const formatINR = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? null
    : `₹${Number(v).toLocaleString("en-IN")}`;

/* The gateway returns the result as a base64-encoded JSON blob in `status`.
   Decode it; if it's just a plain word (e.g. "SUCCESS"), fall back to that. */
function decodeStatusParam(raw) {
  if (!raw) return { data: null, plain: "" };
  try {
    const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64));
    const data = json?.apiResponseData?.data || json?.data || null;
    return { data, plain: String(data?.status || data?.orderStats || "") };
  } catch {
    return { data: null, plain: raw };
  }
}

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const settledRef = useRef(false);
  const [serverStatus, setServerStatus] = useState("");

  const { data, plain, urn, sessionId } = useMemo(() => {
    const decoded = decodeStatusParam(searchParams.get("status") || "");
    return {
      data: decoded.data,
      plain: decoded.plain,
      urn: decoded.data?.urn || searchParams.get("urn") || "",
      sessionId:
        searchParams.get("sessionId") || searchParams.get("session_id") || "",
    };
  }, [searchParams]);

  /* Authoritatively settle server-side by urn (never trust the client blob). */
  useEffect(() => {
    if (!urn || settledRef.current) return;
    settledRef.current = true;
    refreshFinkedaByUrn(urn)
      .then(({ data: r }) => setServerStatus(String(r?.status || "")))
      .catch(() => {});
  }, [urn]);

  const effective = serverStatus || plain;
  const kind = SUCCESS_RE.test(effective)
    ? "success"
    : FAILED_RE.test(effective)
      ? "failed"
      : "pending";

  const meta = {
    success: {
      icon: <CheckCircleRoundedIcon sx={{ fontSize: 64 }} color="success" />,
      title: "Payment Successful",
      subtitle:
        "The card payment went through. The net amount is being settled to the beneficiary's bank account.",
    },
    failed: {
      icon: <CancelRoundedIcon sx={{ fontSize: 64 }} color="error" />,
      title: "Payment Not Completed",
      subtitle:
        "The payment could not be completed. If any amount was debited it will be reversed. You can try again.",
    },
    pending: {
      icon: <HourglassTopRoundedIcon sx={{ fontSize: 64, color: "warning.main" }} />,
      title: "Payment is being confirmed",
      subtitle:
        "We're confirming the status of this payment. Check your payments for the latest status.",
    },
  }[kind];

  const details = [
    ["Reference", urn],
    ["Amount", formatINR(data?.amount)],
    ["Card", data?.maskedData ? `${data.maskedData}${data?.cardNetwork ? ` · ${data.cardNetwork}` : ""}` : null],
    ["Order ID", data?.orderId],
    ["Session ID", !data ? sessionId : null],
  ].filter(([, v]) => v);

  return (
    <Box sx={{ maxWidth: 520, mx: "auto" }}>
      <Card sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: "center" }}>
        {meta.icon}
        <Typography variant="h6" fontWeight={800} mt={1.5}>
          {meta.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5} mb={3}>
          {meta.subtitle}
        </Typography>

        {details.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3, textAlign: "left" }}>
            <Stack spacing={1} divider={<Divider flexItem />}>
              {details.map(([label, value]) => (
                <Stack
                  key={label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  spacing={2}
                >
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
          <Button
            component={RouterLink}
            to="/finkeda/transactions"
            variant="contained"
            startIcon={<ReceiptLongRoundedIcon />}
            sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: "none" }}
          >
            View my payments
          </Button>
          <Button
            component={RouterLink}
            to="/finkeda"
            variant="outlined"
            startIcon={<SendToMobileRoundedIcon />}
            sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: "none" }}
          >
            Make another payment
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
